import { PropsWithChildren, useEffect, useState } from "react";
import { ModalProvider } from "react-modal-hook";
import { RouteComponentProps, StaticContext, withRouter } from "react-router";
import { Redirect, Route, Switch } from "react-router-dom";
import { RESOURCE_NAME } from "common/authorization";
import { SUBMISSION_CATEGORY } from "common/constants";
import { buildLink, getPath } from "common/routing";
import { globalPrintStyles } from "./__styles__/globalStyles";
import { withAuthorization } from "./Authorization/withAuthorization";
import { RouteContainer, RouteContent } from "./Common/__styles__/Routes";
import { SkipNavigation, SkipNavigationContent } from "./Common/SkipNavigation";
import DocumentUploads from "./DocumentUploads";
import AIActionGeneratingDocument from "./DocumentUploads/AIActionGeneratingDocument";
import { DetailView as DocumentUploadDetailPage } from "./DocumentUploads/DetailView";
import CertificateShim from "./DocumentUploads/ElevationCertificates/CertificateShim";
import GeocodeDocument from "./DocumentUploads/GeocodeDocument";
import DownloadExport from "./Exports/DownloadExport";
import Logs from "./Logs";
import CreateLog from "./Logs/LogCreate";
import EditLog from "./Logs/LogEdit";
import Map from "./Maps";
import ObjectsTable from "./ObjectsTable";
import Properties from "./Property";
import CreateProperty from "./Property/CreateProperty";
import EditProperty from "./Property/EditProperty";
import GeocodeRepetitiveLoss from "./RepetitiveLoss/GeocodeRepetitiveLoss";
import Settings from "./Settings";
import EditSection from "./Settings/Account/PropertyData/OverviewSections/EditSection";
import { SubmissionsBuilderV2 } from "./Settings/Account/Records/SubmissionsBuilderV2/SubmissionsBuilderV2";
import BulkUserInviteForm from "./Settings/BulkUserInviteForm";
import CreateOrUpdateDocumentTemplateForm from "./Settings/Data/DocumentTemplates/CreateOrUpdateForm";
import { DispatchBulkDocumentGeneration } from "./Settings/Data/Operations/DispatchBulkDocumentGeneration";
import { DispatchBulkDocumentUploadActionForm } from "./Settings/Data/Operations/DispatchBulkDocumentUploadActionForm";
import DispatchBulkGeocodeForm from "./Settings/Data/Operations/DispatchBulkGeocodeForm";
import DispatchBulkPropertyActionForm from "./Settings/Data/Operations/DispatchBulkPropertyActionForm";
import { DispatchBulkSubmissionActionForm } from "./Settings/Data/Operations/DispatchBulkSubmissionActionForm";
import ExportPropertyAndParcelInformation from "./Settings/Data/Operations/ExportPropertyAndParcelInformation";
import DispatchUpdateAccountBoundaryForm from "./Settings/Data/Operations/UpdateAccountBoundaryForm";
import ParcelImportConfigWizard from "./Settings/Data/Parcels/ParcelImportWizard/ParcelImportConfiguration";
import RepetitiveLossTable from "./Settings/Data/RepetitiveLossImports/RepetitiveLossTable/RepetitiveLossTable";
import SideNav, { SideNavProps, SideNavWidthContext } from "./SideNav";
import CreateSubmissionContainer from "./Submissions/CreateSubmission";
import EditSubmission from "./Submissions/EditSubmission";
import SubmissionTables from "./Submissions/SubmissionsTable";
import SubmissionSummary from "./Submissions/SubmissionSummary";
import ViewSubmission from "./Submissions/ViewSubmission";
import Tasks from "./Tasks";
import WorkflowsPage from "./Workflows/Table";
import { Workflow } from "./Workflows/Workflow";

type ScrollToTopProps = RouteComponentProps<
  {},
  StaticContext,
  Maybe<{ scrollToTop?: string | boolean }>
>;

const ScrollToTop = ({
  children,
  location,
}: PropsWithChildren<ScrollToTopProps>) => {
  useEffect(() => {
    if (!location.state || location.state.scrollToTop?.toString() === "true") {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.state]);

  return <>{children}</>;
};

const ScrollToTopContainer = withRouter(ScrollToTop);

const RoutesWithSideNav = ({ setSideNavWidth }: SideNavProps) => (
  <RouteContainer>
    <SkipNavigation />
    <SideNav setSideNavWidth={setSideNavWidth} />

    <RouteContent>
      <SkipNavigationContent>
        <ScrollToTopContainer>
          <Switch>
            <Redirect exact from="/" to={getPath("map")} />
            <Route path={getPath("map")} component={Map} />
            <Route path={getPath("logs")} component={Logs} />
            <Route
              path={getPath("documentUploads")}
              component={DocumentUploads}
            />
            <Route path={getPath("properties")} component={Properties} />
            <Route path={getPath("settings")} component={Settings} />
            <Route
              path={getPath("recordsTable")}
              component={SubmissionTables}
            />
            <Route path={getPath("objectsTable")} component={ObjectsTable} />
            <Route path={getPath("tasksTable")} component={Tasks} />
            <Route path={getPath("workflowsTable")} component={WorkflowsPage} />

            {/* Redirect legacy URLs */}
            <Redirect from="/assistance-requests" to="/logs" />
            <Redirect from="/crs/logs/:subpage" to="/logs/:subpage" />
            <Redirect from="/crs" to="/logs" />
            <Redirect
              from="/inspections"
              to={buildLink("recordsTable", {
                category: SUBMISSION_CATEGORY.INSPECTIONS,
              })}
            />
            <Redirect
              from="/submissions"
              to={buildLink("recordsTable", {
                category: SUBMISSION_CATEGORY.PERMITTING,
              })}
            />
          </Switch>
        </ScrollToTopContainer>
      </SkipNavigationContent>
    </RouteContent>
  </RouteContainer>
);

const App = () => {
  globalPrintStyles();

  const [sideNavWidth, setSideNavWidth] = useState(0);

  return (
    <SideNavWidthContext.Provider value={{ width: sideNavWidth }}>
      <ModalProvider>
        <Switch>
          <Route
            path="/ai-action-runs/:id"
            component={withAuthorization(AIActionGeneratingDocument, {
              resource: RESOURCE_NAME.AI_ACTION_RUN_RESULT,
              permission: "view",
            })}
          />
          <Route path="/files/:id" component={DocumentUploadDetailPage} />
          {/* Legacy URL */}
          <Redirect from="/documents/:id" to="/files/:id" />
          {/* Legacy URL, and utilized for automation reports */}
          <Route
            path={getPath("viewCertificateForReport")}
            component={CertificateShim}
          />
          <Route
            path={getPath("createProperty")}
            component={withAuthorization(CreateProperty, {
              resource: RESOURCE_NAME.PROPERTY,
              permission: "create",
            })}
          />
          <Route
            path={getPath("geocodeRepetitiveLoss")}
            component={withAuthorization(GeocodeRepetitiveLoss, {
              resource: RESOURCE_NAME.REPETITIVE_LOSS,
              permission: "update",
            })}
          />
          <Route
            path={getPath("geocodeDocument")}
            component={withAuthorization(GeocodeDocument, {
              resource: RESOURCE_NAME.DOCUMENT_UPLOAD,
              permission: "update",
            })}
          />
          <Route
            path={getPath("editProperty")}
            component={withAuthorization(EditProperty, {
              resource: RESOURCE_NAME.PROPERTY,
              permission: "update",
            })}
          />
          <Route
            path={getPath("submissionSummary")}
            component={withAuthorization(SubmissionSummary, {
              resource: RESOURCE_NAME.SUBMISSION,
              permission: "view",
            })}
          />
          <Route
            path={getPath("createSubmission")}
            component={CreateSubmissionContainer}
          />
          <Route
            path={getPath("editSubmission")}
            component={withAuthorization(EditSubmission, {
              resource: RESOURCE_NAME.SUBMISSION,
              // This route is for both view and edit
              // the edit button will be permissioned of edit while loading the page will be permissioned on view
              permission: "view",
            })}
          />
          <Route
            path={getPath("viewSubmission")}
            component={withAuthorization(ViewSubmission, {
              resource: RESOURCE_NAME.SUBMISSION,
              permission: "view",
            })}
          />
          <Route
            path={getPath("repetitiveLosses")}
            // the component is already wrapped with authorization
            // to make sure you can't accidentally use it without auth
            component={RepetitiveLossTable}
          />
          <Route
            path={getPath("createLog")}
            component={withAuthorization(CreateLog, {
              resource: RESOURCE_NAME.INFORMATION_REQUEST,
              permission: "private_create",
            })}
          />
          <Route
            path={getPath("editLog")}
            component={withAuthorization(EditLog, {
              resource: RESOURCE_NAME.INFORMATION_REQUEST,
              permission: "view",
            })}
          />
          <Route
            path={getPath("createDocumentTemplate")}
            component={withAuthorization(CreateOrUpdateDocumentTemplateForm, {
              resource: RESOURCE_NAME.DOCUMENT_TEMPLATE,
              permission: "create",
            })}
          />
          <Route
            path={getPath("updateDocumentTemplate")}
            component={withAuthorization(CreateOrUpdateDocumentTemplateForm, {
              resource: RESOURCE_NAME.DOCUMENT_TEMPLATE,
              permission: "update",
            })}
          />
          <Route
            path={getPath("dispatchBulkPropertyCreation")}
            component={withAuthorization(DispatchBulkPropertyActionForm, {
              resource: RESOURCE_NAME.SCRIPT_JOB,
              permission: "dispatch",
            })}
          />
          <Route
            path={getPath("dispatchBulkSubmissionCreation")}
            component={withAuthorization(DispatchBulkSubmissionActionForm, {
              resource: RESOURCE_NAME.SCRIPT_JOB,
              permission: "dispatch",
            })}
          />
          <Route
            path={getPath("dispatchBulkDocumentUploadCreate")}
            component={withAuthorization(DispatchBulkDocumentUploadActionForm, {
              resource: RESOURCE_NAME.SCRIPT_JOB,
              permission: "dispatch",
            })}
          />
          <Route
            path={getPath("dispatchBulkDocumentGeneration")}
            component={withAuthorization(DispatchBulkDocumentGeneration, {
              resource: RESOURCE_NAME.SCRIPT_JOB,
              permission: "dispatch",
            })}
          />
          <Route
            path={getPath("dispatchUpdateAccountBoundary")}
            component={withAuthorization(DispatchUpdateAccountBoundaryForm, {
              resource: RESOURCE_NAME.SCRIPT_JOB,
              permission: "dispatch",
            })}
          />
          <Route
            path={getPath("dispatchBulkGeocodeCsv")}
            component={withAuthorization(DispatchBulkGeocodeForm, {
              resource: RESOURCE_NAME.SCRIPT_JOB,
              permission: "dispatch",
            })}
          />
          <Route
            path={getPath("exportPropertyAndParcelInfo")}
            component={withAuthorization(ExportPropertyAndParcelInformation, {
              resource: RESOURCE_NAME.SCRIPT_JOB,
              permission: "dispatch",
            })}
          />
          <Route
            path="/settings/bulk-user-invite"
            component={withAuthorization(BulkUserInviteForm, {
              resource: RESOURCE_NAME.USER_INVITE,
              permission: "bulkCreate",
            })}
          />
          <Route
            exact
            path={[
              getPath("parcelImportConfigCreate"),
              getPath("parcelImportConfigEdit"),
            ]}
            component={withAuthorization(ParcelImportConfigWizard, {
              resource: RESOURCE_NAME.PARCEL_IMPORT_CONFIGURATION,
              permission: "create",
            })}
          />
          <Route
            exact
            path={getPath("submissionsBuilderV2")}
            component={SubmissionsBuilderV2}
          />
          <Route path={getPath("editSection")} component={EditSection} />
          <Route path={getPath("downloadExport")} component={DownloadExport} />
          <Route path={getPath("workflow")} component={Workflow} />
          <Route render={() => RoutesWithSideNav({ setSideNavWidth })} />
        </Switch>
      </ModalProvider>
    </SideNavWidthContext.Provider>
  );
};

export default App;
